import { Grid } from '@material-ui/core';
import { BaseEmptyPage, PaddedContent } from 'design-system';
import { useCallback, useEffect, useMemo } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Data Layer
import {
  coverageInfoForProduct,
  endorsementCoverages,
} from 'data-layer-cr/dist/functions/coverages';
import {
  defaultUnit,
  getEndorsementCoverages,
  getRegularCoverages,
  mustShowAllUnits,
} from 'data-layer-cr/dist/functions/quotations';
import {
  ProductCoverageCatalog,
  Quotation,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { getDynamicRenderConfigs } from 'data-layer-cr/dist/slices/dynamicRender/actions/dynamicRenderActions';
import { CONFIG_REQUEST } from 'data-layer-cr/dist/slices/dynamicRender/requestIndicator';

// Redux related
import CustomerItem from 'src/models/helperTypes/CustomerItem';
import { requestCoveragesForProduct } from 'src/store/coverage/actions';
import { GET_COVERAGES_FOR_PRODUCT } from 'src/store/coverage/requestIdentifiers';
import {
  cleanCurrentQuotationReference,
  getPlans,
  makePricing,
  resetCreationFormValues,
  updatePricingForCoverages,
  updateQuotationModalIsOpenValue,
} from 'src/store/quotation/actions';
import {
  CREATE_QUOTATION,
  REQUEST_PRICING,
  REQUEST_PRICING_INITIAL,
} from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';
import { setStepper } from 'src/store/steppers/actions';

// layouts
import Layout from 'src/layout';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import NetworkClient from 'src/services/NetworkClient';

// Methods
import {
  changeCoverageDeductible,
  replaceCoverageValues,
} from 'src/functions/coverageMethods';
import {
  hasUnits,
  purgeUnwantedSimplifiedCoverages,
} from 'src/functions/quotationMethods';
import {
  makeActiveSection,
  makeCoverageNetworkDialogue,
  makeCreateNetworkDialogue,
} from './functions/viewGeneration';

// Components
import { DynamicHeader } from './components';

// Hooks
import { ExemptionCoverageId } from '../components/IndividualCoverages/ExemptionCoverage';
import { useCoverageTracker } from './hooks';

export const DynamicQuotationPage = ({
  currentQuotation,
  updatePricingAction,
  makePricingAction,
  cleanCurrentQuotationReferenceAction,
  coveragesForProduct,
  requestCoveragesForProductAction,
  setStepperAction,
  updateQuotationModalIsOpenValueAction,
  resetCreationFormValuesAction,
  getDynamicRenderConfigsAction,
  currentProfile,
  getPlansAction,
  savedQuotationUnit,
}: PropsFromRedux): JSX.Element => {
  const history = useNavigate();
  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING);
  const requestingQuotationInitialState = useNetworkObserver(
    REQUEST_PRICING_INITIAL,
  );
  const creatingQuotationState = useNetworkObserver(CREATE_QUOTATION);
  const requestingCoverageState = useNetworkObserver(GET_COVERAGES_FOR_PRODUCT);

  useEffect(() => {
    getPlansAction(currentProfile.id);
  }, [getPlansAction, currentProfile.id]);

  // We have to request config again because cloning is possible also
  const requestingConfigState = useNetworkObserver(CONFIG_REQUEST);

  /**
   * The resume list used to display required information
   */
  const { coverageResumes, coverages } = useMemo(
    () =>
      coverageInfoForProduct(coveragesForProduct, currentQuotation.productId),
    [coveragesForProduct, currentQuotation.productId],
  );

  /**
   * Called when the back button is pressed
   */
  function onBackRequested(): void {
    cleanCurrentQuotationReferenceAction();
    history('/');
  }

  /**
   * Called when the close button is pressed
   */
  function onCloseRequested(): void {
    resetCreationFormValuesAction();
    updateQuotationModalIsOpenValueAction(false);
    setStepperAction(0);
    history('/');
  }

  /**
   * Called when a recalculate request needs to be made
   */
  const requestRecalculate = useCallback(
    (newCoverages: QuotationCoverage[], deductibleOption: number): void => {
      const usedCoverages = purgeUnwantedSimplifiedCoverages(
        newCoverages,
        coverageResumes,
      );
      updatePricingAction(
        usedCoverages,
        0,
        deductibleOption,
        currentQuotation.productId,
      );
    },
    [currentQuotation.productId, coverageResumes, updatePricingAction],
  );

  /**
   * Called when endorsement is changed
   */
  const onChangeEndorsement = useCallback(
    (
      modifiedPackage: number,
      newEndorsement: QuotationCoverage,
      deductibleOption: number,
    ): void => {
      const unitCoverages = currentQuotation.units[modifiedPackage].coverages;
      const endorsementCodes = endorsementCoverages(coverages).map(
        (endorsement) => endorsement.code,
      );
      const regularCoverages = unitCoverages.filter(
        (coverage) => !endorsementCodes.includes(coverage.coverageId),
      );
      const usedCoverages = newEndorsement.coverageId
        ? [...regularCoverages, newEndorsement]
        : regularCoverages;
      updatePricingAction(
        usedCoverages,
        modifiedPackage,
        deductibleOption,
        currentQuotation.productId,
      );
    },
    [currentQuotation, updatePricingAction, coverages],
  );

  const onChangeCustomerProfile = useCallback(
    (
      modifiedPackage: number,
      newEndorsement: QuotationCoverage,
      deductibleOption: number,
      packageSelected: string,
    ): void => {
      const unitCoverages =
        savedQuotationUnit.find(
          (item) => item.package.description === packageSelected,
        )?.coverages || [];
      const endorsementCodes = endorsementCoverages(coverages).map(
        (endorsement) => endorsement.code,
      );
      const regularCoverages = unitCoverages.filter(
        (coverage) => !endorsementCodes.includes(coverage.coverageId),
      );
      const usedCoverages = newEndorsement.coverageId
        ? [...regularCoverages, newEndorsement]
        : regularCoverages;
      updatePricingAction(
        usedCoverages,
        modifiedPackage,
        deductibleOption,
        currentQuotation.productId,
        true,
        packageSelected,
      );
    },
    [currentQuotation, updatePricingAction, coverages, savedQuotationUnit],
  );

  const onChangeEndorsementExemption = useCallback(
    (
      modifiedPackage: number,
      newEndorsement: QuotationCoverage,
      deductibleOption: number,
      addExemptionCoverage: boolean,
      packageSelected: string,
    ): void => {
      const unitCoverages = currentQuotation.units[modifiedPackage].coverages;

      const usedCoverages = addExemptionCoverage
        ? [...unitCoverages, newEndorsement]
        : unitCoverages.filter(
            (item) => item.coverageId !== ExemptionCoverageId,
          );

      updatePricingAction(
        usedCoverages,
        modifiedPackage,
        deductibleOption,
        currentQuotation.productId,
        true,
        packageSelected,
      );
    },
    [currentQuotation, updatePricingAction],
  );

  /**
   * Most of the coverage management logic was encapsulated inside this hook.
   */
  const {
    selectedEndorsement,
    selectedDeductibleOption,
    updateEndorsement,
    overwriteAll,
    updateEndorsementExemption,
    updateCustomerProfile,
  } = useCoverageTracker(
    defaultUnit(currentQuotation).deductibleOption,
    mustShowAllUnits(currentQuotation),
    requestRecalculate,
    onChangeEndorsement,
    onChangeCustomerProfile,
    onChangeEndorsementExemption,
  );

  /**
   * Called when coverage limit is changed
   */
  const onChangeCoverageLimit = useCallback(
    (
      modifiedPackage: number,
      modifiedCoverages: QuotationCoverage[],
      shouldUpdate: boolean,
      packageSelected: string,
    ): void => {
      const unitCoverages =
        currentQuotation.units.find(
          (item) => item.package.description === packageSelected,
        )?.coverages || [];
      const usedCoverages = replaceCoverageValues(
        unitCoverages,
        modifiedCoverages,
      );
      updatePricingAction(
        usedCoverages,
        modifiedPackage,
        selectedDeductibleOption,
        currentQuotation.productId,
        shouldUpdate,
        packageSelected,
      );
    },
    [currentQuotation, updatePricingAction, selectedDeductibleOption],
  );

  /**
   * Called when deductible option is changed
   */
  const onChangeDeductibleOption = useCallback(
    (modifiedPackage: number, deductibleOption: number): void => {
      const unitCoverages = currentQuotation.units[modifiedPackage].coverages;
      const usedCoverages = changeCoverageDeductible(
        unitCoverages,
        deductibleOption,
        currentProfile.typeOfMoney,
      );
      updatePricingAction(
        usedCoverages,
        modifiedPackage,
        selectedDeductibleOption,
        currentQuotation.productId,
      );
    },
    [
      currentQuotation,
      updatePricingAction,
      selectedDeductibleOption,
      currentProfile,
    ],
  );

  /**
   * This is the component that will be renderd as the page's "content"
   */
  const activeSection = useMemo(
    () =>
      makeActiveSection({
        productType: currentQuotation.productId,
        selectedEndorsement,
        updateEndorsement,
        updateEndorsementExemption,
        updateCustomerProfile,
        onChangeCoverageLimit,
        onChangeDeductible: onChangeDeductibleOption,
      }),
    [
      currentQuotation.productId,
      selectedEndorsement,
      updateCustomerProfile,
      updateEndorsement,
      onChangeCoverageLimit,
      onChangeDeductibleOption,
      updateEndorsementExemption,
    ],
  );

  /**
   * Since the data on which coverages are "regular" and which are "endorsements" is
   * obtained from the server after the page finishes loading, we cannot directly set
   * `endorsement` and `simplifiedCoverages` initial values until the catalog request
   * finishes. This is why we need to listen to this effect to set the initial values,
   * instead of directly setting them on the `useState` definition.
   */
  useEffect(() => {
    if (hasUnits(currentQuotation)) {
      const regularCoverages = getRegularCoverages(
        currentQuotation,
        coverageResumes,
      );
      const [endorsement] = getEndorsementCoverages(
        currentQuotation,
        coverageResumes,
      );
      overwriteAll(regularCoverages, endorsement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuotation, coverageResumes]);

  /**
   * Redirect to details page on success
   */
  useEffect(() => {
    if (creatingQuotationState === 'success') {
      history('/quotationDetails');
    }
  }, [creatingQuotationState, history]);

  /**
   * Always refresh the coverage for product when:
   * - The screen first renders.
   * - The product changes.
   * Note: we have to check through quotationProfile.id instead of productId
   * because we have to wait for VehiclePricing to finish before refreshing
   * coverage for product.
   *
   */

  useEffect(() => {
    if (currentQuotation.productId !== '') {
      requestCoveragesForProductAction(
        currentQuotation.productId,
        currentQuotation.units[0].unit.use,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestCoveragesForProductAction, currentQuotation.quotationProfile.id]);

  useEffect(() => {
    getDynamicRenderConfigsAction(
      currentQuotation.quotationProfile.id.toString(),
      NetworkClient,
    );
  }, [currentQuotation.quotationProfile.id, getDynamicRenderConfigsAction]);

  /** Memoized network dialogue instances */
  const createNetworkDialogue = useMemo(
    () => makeCreateNetworkDialogue(creatingQuotationState),
    [creatingQuotationState],
  );
  const coverageNetworkDialogue = useMemo(
    () =>
      makeCoverageNetworkDialogue(
        requestingCoverageState,
        requestingConfigState,
      ),
    [requestingCoverageState, requestingConfigState],
  );

  /** If no valid quotation is found, Navigate */
  if (!hasUnits(currentQuotation)) {
    history('/');
  }

  return (
    <Layout productId={currentQuotation.productId}>
      <BaseEmptyPage>
        {/* Quotation summary */}
        <DynamicHeader
          data-testid="dynamic-header"
          onBackRequested={onBackRequested}
          onCloseRequested={onCloseRequested}
          loading={
            requestingQuotationState === 'requesting' ||
            requestingQuotationInitialState === 'requesting'
          }
          onCreateQuotation={(): void => {
            makePricingAction(
              selectedDeductibleOption,
              currentQuotation.productId,
              true,
            );
            setStepperAction(0);
          }}
        />
        <PaddedContent defaultPadding={0}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} style={{ marginTop: '40px' }}>
              {activeSection}
            </Grid>
          </Grid>
        </PaddedContent>
        {createNetworkDialogue}
        {coverageNetworkDialogue}
      </BaseEmptyPage>
    </Layout>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  currentQuotation: Quotation;
  currentCustomer: CustomerItem;
  coveragesForProduct: ProductCoverageCatalog;
  currentProfile: PricingProfileResponse;
  savedQuotationUnit: QuotationUnit[];
} => ({
  currentQuotation: state.quotation.currentQuotation,
  coveragesForProduct: state.coverage.coveragesForProduct,
  currentCustomer: state.customer.currentCustomer,
  currentProfile: state.quotation.currentProfile,
  savedQuotationUnit: state.quotation.savedQuotationUnit,
});

const reduxActions = {
  updatePricingAction: updatePricingForCoverages,
  getPlansAction: getPlans,
  makePricingAction: makePricing,
  resetCreationFormValuesAction: resetCreationFormValues,
  cleanCurrentQuotationReferenceAction: cleanCurrentQuotationReference,
  requestCoveragesForProductAction: requestCoveragesForProduct,
  setStepperAction: setStepper,
  updateQuotationModalIsOpenValueAction: updateQuotationModalIsOpenValue,
  getDynamicRenderConfigsAction: getDynamicRenderConfigs,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DynamicQuotationPage);
